import { useAtom } from 'jotai';
import { mapValues } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { SearchableSelectConfig } from '@/components/SearchableSelect';
import {
  DEFAULT_TIME_FILTER,
  type TimePresetKeyWithField
} from '@/components/TimeFilter';
import {
  ticketFiltersAtom,
  ticketTimeFilterAtom
} from '@/pages/Tickets/Tickets.store';
import { safeJsonParse } from '@/utils/Common.utils';

import type { SearchFilters } from './SearchBar.types';

export const SEARCH_SEPARATOR_CHAR = '|';

export const useQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsObj = Object.fromEntries(searchParams);
  const { enqueueSnackbar } = useSnackbar();

  const validateSearchParams = (): Record<string, string> => {
    // Check if the 'q' parameter exists
    const params = searchParams.get('q');
    if (!params) {
      return {};
    }

    try {
      const parsed = JSON.parse(params);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        throw new Error('Invalid query format');
      }

      return parsed;
    } catch {
      enqueueSnackbar(
        'Invalid query format. Please check the URL and try again.',
        {
          style: { whiteSpace: 'pre-line' },
          variant: 'error',
          preventDuplicate: true
        }
      );
      navigate(location.pathname);
      return {};
    }
  };

  // Validate query parameters on mount and changes
  useEffect(() => {
    validateSearchParams();
  }, [searchParams]);

  const updateSearchParams = (newParams: Record<string, string>) => {
    setSearchParams(new URLSearchParams({ ...searchParamsObj, ...newParams }));
  };

  // clear the q param and set the key/value pair in the q param
  const setFilter = (key: string, value: string | string[]) => {
    const newValue = Array.isArray(value)
      ? value.join(SEARCH_SEPARATOR_CHAR)
      : value;
    updateSearchParams({
      q: JSON.stringify({ [key]: newValue })
    });
  };

  // add a key/value pair to the q param
  const addFilter = (key: string, value: string | string[]) => {
    const currentFilters = safeJsonParse(searchParams.get('q'));
    const newValue = Array.isArray(value)
      ? value.join(SEARCH_SEPARATOR_CHAR)
      : value;

    updateSearchParams({
      q: JSON.stringify({ ...currentFilters, [key]: newValue })
    });
  };

  // remove a key/value pair from the q param
  const removeFilter = (key: string) => {
    const currentFilters = safeJsonParse(searchParams.get('q'));
    delete currentFilters[key];
    updateSearchParams({
      q: JSON.stringify(currentFilters)
    });
  };

  // remove a specific filter value. If the value is the only value for the key, remove the key/value pair
  const removeFilterValue = (key: string, value: string) => {
    const currentFilters = safeJsonParse(searchParams.get('q'));

    if (!currentFilters[key]) return;

    if (currentFilters[key] === value) {
      delete currentFilters[key];
    } else {
      const values = currentFilters[key].split(SEARCH_SEPARATOR_CHAR);
      const filteredValues = values.filter((v: string) => v !== value);

      if (filteredValues.length === 0) {
        delete currentFilters[key];
      } else {
        currentFilters[key] = filteredValues.join(SEARCH_SEPARATOR_CHAR);
      }
    }

    updateSearchParams({
      q: JSON.stringify(currentFilters)
    });
  };

  // clear all query params
  const clearParams = () => {
    setSearchParams(new URLSearchParams());
  };

  // clear the search param (q)
  const clearFilters = () => {
    updateSearchParams({ q: '{}' });
  };

  // set the time param (t) to a key/value pair like last:1h or custom:2021-01-01T00:00:00.000Z-2021-01-02T00:00:00.000Z
  const setTimeFilter = (key: TimePresetKeyWithField) => {
    updateSearchParams({ t: key });
  };

  // clear the time param (t)
  const clearTimeFilter = () => {
    const { t: _t, ...rest } = searchParamsObj;
    setSearchParams(new URLSearchParams(rest));
  };

  // Process filters
  const currentFilters = safeJsonParse(searchParams.get('q'));
  const filters = Object.fromEntries(
    Object.entries(currentFilters).map(([key, value]) => [
      key,
      typeof value === 'string' && value.includes(SEARCH_SEPARATOR_CHAR)
        ? value.split(SEARCH_SEPARATOR_CHAR)
        : [value as string]
    ])
  );

  const timeFilter = searchParams.get('t') ?? DEFAULT_TIME_FILTER;

  return {
    searchParams,
    searchParamsObj,
    location,
    filters,
    setSearchParams,
    clearParams,
    clearFilters,
    setFilter,
    addFilter,
    removeFilter,
    removeFilterValue,
    setTimeFilter,
    timeFilter,
    clearTimeFilter
  };
};

export const useFilterLabels = (filters: SearchableSelectConfig[]) => {
  const filterLabels = useMemo(() => {
    const labels: Record<string, string> = { any: 'Any String' };
    filters.forEach((filter) => {
      labels[filter.field] = filter.title;
    });
    return labels;
  }, [filters]);

  const getFilterOptionLabel = (key: string, value: string) => {
    const filteredFilter = filters.find((filter) => filter.field === key);
    if (filteredFilter) {
      const option = filteredFilter.filterOptions.find(
        (o) => o.value === value
      );
      if (option) return option.label;
    }
    return value;
  };

  return { filterLabels, getFilterOptionLabel };
};

export const parseSearchFiltersToQueryParams = (
  filters: SearchFilters
): string => {
  const searchParams = mapValues(filters, (value) =>
    value.join(SEARCH_SEPARATOR_CHAR)
  );

  return JSON.stringify(searchParams);
};

export const usePreviousSearchParams = () => {
  const [ticketFilters] = useAtom(ticketFiltersAtom);
  const [ticketTimeFilter] = useAtom(ticketTimeFilterAtom);

  const searchParams = useMemo(
    () => ({
      t: ticketTimeFilter,
      q: parseSearchFiltersToQueryParams(ticketFilters || {})
    }),
    [ticketFilters, ticketTimeFilter]
  );

  return searchParams;
};

export default useQueryParams;
